import { Icons } from '@tecalisdev/infinity';
import {
	CONNECTION_TYPE,
	IOperation,
	OPERATION_TYPE,
	POL_TEST,
	POL_TYPE,
} from '../../operations/models/operations.models';
import { IMetaResponse } from '../../shared/models/master.models';
import { RetentionDurationApiValue } from '../../shared/utils/retention-duration.util';

export interface ITextLocaleMap {
	[key: string]: string;
}

export type TwoFactorChannel = 'sms' | 'whatsapp';
export type TwoFactorType = TwoFactorChannel;
export type AuthChannel = 'none' | TwoFactorChannel;

export interface IAuthTfaPayload {
	phone: string;
	type: TwoFactorType;
}

export interface IWorkflowTwoFactorConfig {
	type: TwoFactorType;
}

export interface IWorkflowsListResponse {
	data: IWorkflowsListBody[];
	meta: IMetaResponse;
}

export interface IWorkflowsListBody {
	alias: string;
	api_key: string;
	api_key_alias: string;
	api_key_uuid: string;
	company_uuid?: string;
	created_at: string;
	operations?: string[];
	status: string;
	updated_at: string;
	uuid: string;
}

export interface IWorkflowsListPayload {
	search?: string;
	company_uuid?: string;
	start_at?: string;
	end_at?: string;
	status?: 'active' | 'inactive' | 'draft';
	sort_order?: string;
	sort_by?: string;
	per_page?: string;
	page?: string;
	unpaginated?: boolean;
}

export enum WorkflowStep {
	GENERAL = 'general',
	DESIGN = 'design',
	OPERATIONS = 'operations',
	CONFIRMATION = 'confirmation',
}
export type TWorkflowsSteps = WorkflowStep.GENERAL | WorkflowStep.OPERATIONS | WorkflowStep.CONFIRMATION;

export type WorkflowValidationProcessType = 'auto' | 'ok' | 'ko' | 'ok-ko';

export interface IWorkflowValidationTypeProcessErrors {
	type: WorkflowValidationProcessType;
	// Array of uuid identifiers of the errors numbers to review manually.
	// When all errors are selected, the API can receive an empty list.
	errors: string[];
}

/**
 * Contract de `config.validation.type` (API):
 * `{ type: 'auto'|'ok'|'ko'|'ok-ko', errors: string[] }`
 * UI: `auto` → Automática; `ok`|`ko`|`ok-ko` → Manual (+ subtipo / errores).
 */
export type WorkflowValidationType = IWorkflowValidationTypeProcessErrors;

/** @deprecated NIF-748: importar desde `settings/pages/error-catalog/models/error-catalog.models` */
export type {
	IErrorsNumbersItem,
	IErrorsNumbersListResponse,
	IErrorsNumberFormPayload as IErrorsNumberUpsertPayload,
	IErrorsNumbersItem as IErrorsNumberCatalogItem,
} from '../../settings/pages/error-catalog/models/error-catalog.models';

export interface IWorkflowsCreatePayload {
	alias: string;
	api_key: string;
	config: {
		allowed_device: Array<'mobile' | 'pc'>;
		version: number;
		metadata: any;
		has_saved_state?: boolean;
		required_recording: boolean;
		include_images: boolean;
		token_duration_minutes?: number | null;
		location?: 'disabled' | 'enabled' | 'mandatory';
		document_retention_days?: RetentionDurationApiValue;
		media_retention_days?: RetentionDurationApiValue;
		info_retention_days?: RetentionDurationApiValue;
		delayed_process_warning: boolean;
		delayed_process_warning_visibility?: 'start_and_resume' | 'resume_only';
		/** Legacy: string; API futuro: mapa por locale. */
		delayed_process_warning_text: string | ITextLocaleMap;
		antifraud?: {
			ip_block?: boolean;
			ip_block_global?: boolean;
			identifier_block?: boolean;
			identifier_block_global?: boolean;
			face_block?: boolean;
			face_block_global?: boolean;
			block_operation?: boolean;
			email_alert?: string;
		};
		notify: {
			email: {
				to: string;
				template: string;
				subject: string;
				from: string;
				from_name: string;
			};
			sms: {
				phone: string;
				sms_text: string;
				sms_from: string;
			};
		};
		tfa?: IWorkflowTwoFactorConfig;
		sdk: boolean;
		validation: {
			type: WorkflowValidationType;
			emails: string;
		};
	};
	design_config_uuid: string;
	operations: Array<IOperation>;
}

export interface IWorkflowsCreateResponse {
	api_key?: string;
	profile_uuid?: string;
	short_url_pwcs?: boolean;
}

export interface ITermsAndConditionsForm {
	custom_id: string;
	html: any;
	required: boolean;
	id?: any;
	uuid?: string;
}

export enum TAB_WORKFLOW_DETAIL {
	DETAILS = 'details',
	OPERATIONS = 'operations',
}

export enum TAB_WORKFLOW_OCR_FRAUD {
	PHOTOCOPY = 'photocopy',
	SCREENPHOTO = 'screenphoto',
	MANIPULATED_PHOTO = 'manipulated_photo',
	IDENTIFIER_BLOCKED = 'identifier_blocked',
}

export enum TAB_WORKFLOW_DOC_DETAILS {
	CONFIGURATION = 'configuration',
	INSTRUCTIONS = 'instructions',
}

export interface IOperationInfo {
	type: OPERATION_TYPE;
	disabled: boolean;
	show: boolean;
	selected: boolean;
	icon: Icons;
	title: string;
	image: string;
	description: string;
}

export interface IDocInfo {
	title: string;
	description: string;
	type: any;
	icon: Icons;
	category?: 'generic' | 'company' | 'personal';
	uuid: string;
	confRecommended: {
		total_tries: number;
		min: number;
		max: number;
		preview_validation: boolean;
		match_data: boolean;
		connection_available: boolean;
		connection_type?: CONNECTION_TYPE;
		connection_uuid?: string;
	};
}

export interface IPolDemandLevel {
	id: DEMAND_LEVEL;
	total_test: number;
	total_tries: number;
	type: POL_TYPE;
	scoring: string;
	available_tests: Array<POL_TEST>;
	max_time: number;
}

export enum OCR_DOC_COUNTRY_TYPE {
	ALL = 'all',
	INCLUDE = 'include',
	EXCLUDE = 'exclude',
}

export enum DEMAND_LEVEL {
	RECOMMENDED = 'recommended',
	CUSTOM = 'custom',
	MAX_RIGOR = 'max_rigor',
	STANDARD = 'standard',
	MAX_FLUID = 'max_fluid',
}

export enum CONFIDENCE_THRESHOLD {
	MAX = 'max',
	STANDARD = 'standard',
	CUSTOM = 'custom',
}

export interface IWorkflowAuthResponse {
	message: string;
	expireAt: number;
	process: string;
	company: string;
	pwcs_url: string;
}

export interface IWorkflowAuthPayload {
	api_key: string;
	profile_uuid: string;
	short_url_pwcs: boolean;
	token_duration_minutes?: number;
	/** Origen de la llamada: Identity Admin envía `admin`. */
	origin?: 'admin' | 'api' | null;
	match_data?: IWorkflowAuthMatchDataPayload;
	tfa?: IAuthTfaPayload;
	replacements?: {
		notify: any;
		email_alert: string; // (Antifraude)
		redirect: string;
		redirect_ko: string;
		redirect_cancelled: string;
		callback: string;
		callback_ko: string;
		callback_cancelled: string;
	};
}

export interface IWorkflowAuthMatchDataPayload {
	iban: string; // (Bank scoring)
	identifier: string; // (Borme)
	country: string;
	birthdate: string;
	gender: string;
	expiration_date: string;
	nationality: string;
	surnames: string; // (if bankScoring and not operation ocr)
	names: string; // (if bankScoring and not operation ocr)
	address: string;
	municipality: string;
	postal_code: string;
	province: string;
	id_document_type?: string;
	address_number?: string;
	address_floor?: string;
	address_door?: string;
	cif: string; // (Borme)
	/** NIE para match con carta verde (OCR `required_check_nie`) */
	nie?: string;
}

export interface IWorkflowDetailResponse {
	uuid: string;
	alias: string;
	api_key: string;
	company_uuid?: string;
	company_name?: string;
	config: {
		allowed_device: Array<'pc' | 'mobile'>;
		version: number;
		metadata: any;
		location: 'mandatory' | 'enabled' | 'disabled';
		include_images: boolean;
		token_duration_minutes?: number | null;
		document_retention_days?: RetentionDurationApiValue;
		media_retention_days?: RetentionDurationApiValue;
		info_retention_days?: RetentionDurationApiValue;
		required_recording: boolean;
		has_saved_state: boolean;
		delayed_process_warning: boolean;
		delayed_process_warning_visibility?: 'start_and_resume' | 'resume_only';
		notify: {
			email: {
				to: string;
				template: string;
				subject: string;
				from: string;
				from_name: string;
			};
			sms: {
				phone: string;
				sms_text: string;
				sms_from: string;
			};
		};
		tfa?: IWorkflowTwoFactorConfig;
		antifraud: {
			ip_block: boolean;
			ip_block_global: boolean;
			identifier_block: boolean;
			identifier_block_global: boolean;
			face_block: boolean;
			face_block_global: boolean;
			email_alert: string;
			block_operation: boolean;
		};
		validation: {
			type: WorkflowValidationType;
			emails: string;
		};
		sdk: boolean;
		delayed_process_warning_text: string | ITextLocaleMap;
	};
	design: {
		uuid?: string;
		skin: 'dark' | 'light';
		logo: string;
		css_config: any;
		title: string;
		theme_color: string;
		watermark: boolean;
	};
	operations: Array<IOperation>;
	created_at?: string;
	updated_at?: string;
	status?: 'active' | 'inactive' | 'draft';
	average?: number;
	process?: {
		total: number;
		OK: {
			count: number;
			percent: number;
		};
		KO: {
			count: number;
			percent: number;
		};
		CANCELLED: {
			count: number;
			percent: number;
		};
	};
}
